import { useState } from "react";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "./icon";

// Password field with a show/hide toggle and an optional strength meter,
// ported from the prototype's account.jsx `PasswordInput` + `.pw-meter`.
// Used by the Settings → Security password-change form AND (via the additive
// `icon` prop) by the auth pages (login / reset / accept-invite), which need a
// leading lock icon and the slightly different `.input-trail` toggle geometry.
//
// TWO RENDER MODES, selected by whether `icon` is supplied — see the reconciliation
// note below. With NO new props the output is byte-identical to the original kit
// component, so the existing SecuritySection callers are unaffected.

export interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible label for the field (the show/hide button is labelled separately). */
  ariaLabel?: string;
  autoComplete?: "current-password" | "new-password";
  className?: string;
  /**
   * Leading icon (e.g. `"lock"` on the auth pages). When supplied, the field renders in
   * the auth geometry: a leading `.input-icon` + the `.input-trail` / `.has-trail` toggle
   * (32px, right:5px, native-reveal glyph hidden). When omitted, the field renders in the
   * original kit geometry (`.pw-wrap` / `.pw-toggle`, 30px, right:6px) byte-for-byte.
   */
  icon?: IconName;
  /** i18n label for the show/hide button when the password is hidden. Defaults to the
   *  original hard-coded English so existing callers are unchanged. */
  showLabel?: string;
  /** i18n label for the show/hide button when the password is shown. Defaults to the
   *  original hard-coded English so existing callers are unchanged. */
  hideLabel?: string;
  /** Marks the field invalid: sets `aria-invalid` and the shared `.input-error` border.
   *  Only emitted when explicitly passed, so existing callers stay byte-identical. */
  invalid?: boolean;
  id?: string;
  autoFocus?: boolean;
}

export function PasswordInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  autoComplete,
  className,
  icon,
  showLabel = "Show password",
  hideLabel = "Hide password",
  invalid,
  id,
  autoFocus,
}: PasswordInputProps) {
  const [show, setShow] = useState(false);
  const toggleLabel = show ? hideLabel : showLabel;

  // `icon` selects the auth geometry; omitting it keeps the original kit geometry.
  const inputClass = icon
    ? cn("input has-trail", invalid && "input-error")
    : cn("input", invalid && "input-error");

  return (
    <div className={cn("input-wrap has-icon", !icon && "pw-wrap", className)}>
      {icon && <Icon name={icon} size={16} className="input-icon" />}
      <input
        id={id}
        className={inputClass}
        type={show ? "text" : "password"}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={invalid}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className={icon ? "input-trail" : "pw-toggle"}
        aria-label={toggleLabel}
        onClick={() => setShow((s) => !s)}
      >
        <Icon name={show ? "eyeOff" : "eye"} size={17} />
      </button>
    </div>
  );
}

/**
 * Pure 0–4 strength score: length (8 / 12), mixed case+digit, symbol.
 * Kept side-effect free so it can be unit-tested and reused server-side.
 */
export function passwordStrength(pw: string): number {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[0-9]/.test(pw) && /[a-z]/i.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

const STRENGTH_VAR = [
  "var(--danger)",
  "var(--danger)",
  "var(--warning)",
  "var(--info)",
  "var(--success)",
];

export interface PasswordStrengthProps {
  /** The password to score, or a precomputed 0–4 score. */
  value: string;
  /** Localised labels indexed by score 0–4 (weak…strong). */
  labels: [string, string, string, string, string];
}

export function PasswordStrength({ value, labels }: PasswordStrengthProps) {
  const score = passwordStrength(value);
  const color = STRENGTH_VAR[score];
  return (
    <div className="pw-strength">
      <div className="pw-meter">
        {[0, 1, 2, 3].map((i) => (
          <i key={i} style={{ background: i < score ? color : "var(--surface-3)" }} />
        ))}
      </div>
      <span className="pw-strength-label" style={{ color }}>
        {labels[score]}
      </span>
    </div>
  );
}
